import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
} from '@mui/material'
import ContainedButton, {
  ContainedButtonVariant,
} from '../Buttons/ContainedButton'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import DebouncedAutocomplete from '../Elements/DebouncedAutocomplete'

interface PotentialSupervisor {
  firstName: string
  lastName: string
  roleName: string
  actorKey: number
}

interface ReassignSupervisorModalProps {
  open: boolean
  onClose: () => void
  onReassign: () => void
  selectedCount: number
  selectedSupervisor: PotentialSupervisor | null
  setSelectedSupervisor: (supervisor: PotentialSupervisor | null) => void
  supervisorOptions: PotentialSupervisor[]
  isLoading: boolean
  isReassigning: boolean
  onInputChange: (newValue: string) => void
}

const ReassignSupervisorModal: React.FC<ReassignSupervisorModalProps> = ({
  open,
  onClose,
  onReassign,
  selectedCount,
  selectedSupervisor,
  setSelectedSupervisor,
  supervisorOptions,
  isReassigning,
  onInputChange,
}) => {
  const { t } = useTranslation()
  const showOnDesktop = useShowOnDesktop()

  return (
    <Dialog
      open={open}
      onClose={!isReassigning ? onClose : undefined}
      aria-labelledby="reassign-supervisor-dialog-title"
    >
      <DialogTitle id="reassign-supervisor-dialog-title">
        {t('Team.Reassign.ModalTitle', 'Reassign to New Support')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          {t(
            'Team.Reassign.ModalDescription',
            `You are about to reassign {{count}} team member(s) to a new support. This action cannot be undone.`,
            { count: selectedCount }
          )}
        </Typography>
        <Box mt={2}>
          <DebouncedAutocomplete
            id="supervisor-select"
            options={supervisorOptions}
            optionsLabel={(option) =>
              `${option.firstName} ${option.lastName} (${option.roleName} - ${option.actorKey})`
            }
            noOptionsTextValue={t('Team.Reassign.NoOptions', 'No Options')}
            renderOptions={(
              props: React.HTMLAttributes<HTMLLIElement>,
              option: PotentialSupervisor
            ) => (
              <Box component="li" {...props} key={option.actorKey}>
                {option.firstName} {option.lastName} ({option.roleName})
              </Box>
            )}
            initialValue={selectedSupervisor}
            handleChange={(newValue: PotentialSupervisor | null) =>
              setSelectedSupervisor(newValue)
            }
            updateSearchResults={(search) => {
              onInputChange(search)
            }}
            textFieldLabelValue={t(
              'Team.Reassign.SupervisorSelect',
              'Select New Support'
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={isReassigning}>
          {t('Common.Cancel', 'Cancel')}
        </Button>
        <ContainedButton
          id="reassignTeamMembers"
          variant={ContainedButtonVariant.ReassignTeamMembers}
          disabled={!selectedSupervisor || isReassigning}
          onClick={onReassign}
          fullWidth={!showOnDesktop}
        />
      </DialogActions>
    </Dialog>
  )
}

export default ReassignSupervisorModal
